/**
 * Cloudflare Worker 通用多人房间信令客户端 (Multi-Peer Signaling Client)
 */

import type {
  SignalingClientMessage,
  SignalingServerMessage,
  PKGamePacket,
  GameMode,
} from "../../shared/protocol";

export type SignalingEventMap = {
  connected: () => void;
  disconnected: () => void;
  room_created: (data: { roomId: string; role: "host"; peerId?: string }) => void;
  room_joined: (data: { roomId: string; role: "guest"; opponentName: string; peerId?: string }) => void;
  peer_joined: (data: { roomId: string; role: "host" | "guest"; opponentName: string; peerId?: string }) => void;
  peer_left: (reason: string, peerId?: string) => void;
  room_peers_sync: (data: {
    roomId: string;
    hostId: string;
    peers: Array<{ peerId: string; name: string }>;
    eventText?: string;
  }) => void;
  room_error: (message: string) => void;
  signal_offer: (offer: any) => void;
  signal_answer: (answer: any) => void;
  signal_ice: (candidate: any) => void;
  relay_message: (payload: PKGamePacket, fromPeerId?: string) => void;
  latency_update: (latencyMs: number) => void;
};

export class SignalingClient {
  private ws: WebSocket | null = null;
  private baseUrl: string;
  private activeUrl: string;
  private listeners: { [K in keyof SignalingEventMap]?: Function[] } = {};
  private pingTimer: number | null = null;
  private currentRoomId: string | null = null;
  private lastRole: "host" | "guest" = "host";
  private lastPlayerName: string = "数字猎手";
  private isIntentionalClose = false;
  public readonly peerId: string;

  constructor(customWsUrl?: string) {
    let savedId = "";
    try {
      savedId = sessionStorage.getItem("pk2048_peer_id") || "";
      if (!savedId) {
        savedId = `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
        sessionStorage.setItem("pk2048_peer_id", savedId);
      }
    } catch (_) {
      savedId = `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    }
    this.peerId = savedId;

    const envUrl = (import.meta as any).env?.VITE_SIGNALING_URL;
    if (customWsUrl) {
      this.baseUrl = customWsUrl;
    } else if (envUrl) {
      this.baseUrl = envUrl;
    } else {
      const isHttps = window.location.protocol === "https:";
      const proto = isHttps ? "wss:" : "ws:";
      this.baseUrl = `${proto}//${window.location.host}/ws`;
    }
    this.activeUrl = this.baseUrl;
  }

  public connect(url?: string): Promise<void> {
    if (url) this.activeUrl = url;
    return new Promise((resolve, reject) => {
      this.isIntentionalClose = false;
      try {
        if (this.ws) {
          const oldWs = this.ws;
          this.ws = null;
          oldWs.onopen = null;
          oldWs.onclose = null;
          oldWs.onerror = null;
          oldWs.onmessage = null;
          try {
            oldWs.close();
          } catch (_) {}
        }

        this.ws = new WebSocket(this.activeUrl);

        this.ws.onopen = () => {
          this.startHeartbeat();
          this.emit("connected");
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onclose = () => {
          this.stopHeartbeat();
          this.emit("disconnected");
          if (!this.isIntentionalClose) {
            setTimeout(() => {
              if (!this.isIntentionalClose) this.connect().catch(() => {});
            }, 2000);
          }
        };

        this.ws.onerror = (err) => {
          console.warn("[Signaling] Connection warning/error", err);
        };
      } catch (err) {
        reject(err);
      }
    });
  }

  public connectToRoom(
    roomId: string,
    role: "host" | "guest",
    playerName: string,
    gameMode: GameMode = "classic_2048"
  ): Promise<void> {
    this.currentRoomId = roomId;
    this.lastRole = role;
    this.lastPlayerName = playerName;

    const sep = this.baseUrl.includes("?") ? "&" : "?";
    const targetUrl = `${this.baseUrl}${sep}room=${encodeURIComponent(roomId)}&peerId=${encodeURIComponent(
      this.peerId
    )}&role=${role}&name=${encodeURIComponent(playerName)}&mode=${encodeURIComponent(gameMode)}`;
    return this.connect(targetUrl);
  }

  public disconnect() {
    this.isIntentionalClose = true;
    this.stopHeartbeat();
    if (this.ws) {
      if (this.currentRoomId) {
        this.send({ type: "leave", roomId: this.currentRoomId });
      }
      this.ws.close();
      this.ws = null;
    }
    this.currentRoomId = null;
  }

  public on<K extends keyof SignalingEventMap>(event: K, handler: SignalingEventMap[K]) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event]!.push(handler);
  }

  public off<K extends keyof SignalingEventMap>(event: K, handler: SignalingEventMap[K]) {
    const list = this.listeners[event];
    if (list) {
      this.listeners[event] = list.filter((h) => h !== handler);
    }
  }

  private emit<K extends keyof SignalingEventMap>(event: K, ...args: Parameters<SignalingEventMap[K]>) {
    const list = this.listeners[event];
    if (list) {
      for (const h of list) {
        try {
          (h as any)(...args);
        } catch (e) {
          console.error(`[Signaling] Listener error on ${event}:`, e);
        }
      }
    }
  }

  private send(msg: SignalingClientMessage) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  public reconnectRoom(roomId: string, role: "host" | "guest", playerName: string) {
    this.currentRoomId = roomId;
    this.send({ type: "reconnect_room", roomId, role, playerName });
  }

  public updateRoomMeta(gameMode: GameMode) {
    if (!this.currentRoomId) return;
    this.send({ type: "update_room_meta", roomId: this.currentRoomId, gameMode });
  }

  public sendOffer(offer: any) {
    if (!this.currentRoomId) return;
    this.send({ type: "signal_offer", roomId: this.currentRoomId, offer });
  }

  public sendAnswer(answer: any) {
    if (!this.currentRoomId) return;
    this.send({ type: "signal_answer", roomId: this.currentRoomId, answer });
  }

  public sendIceCandidate(candidate: any) {
    if (!this.currentRoomId) return;
    this.send({ type: "signal_ice", roomId: this.currentRoomId, candidate });
  }

  public sendRelay(payload: PKGamePacket, targetPeerId?: string) {
    if (!this.currentRoomId) return;
    this.send({ type: "relay", roomId: this.currentRoomId, targetPeerId, payload });
  }

  private handleMessage(data: string) {
    try {
      const msg = JSON.parse(data) as SignalingServerMessage;
      switch (msg.type) {
        case "pong": {
          const latency = Math.max(1, Date.now() - msg.clientTime);
          this.emit("latency_update", latency);
          break;
        }

        case "room_created": {
          this.currentRoomId = msg.roomId;
          this.emit("room_created", { roomId: msg.roomId, role: msg.role, peerId: msg.peerId });
          break;
        }

        case "room_joined": {
          this.currentRoomId = msg.roomId;
          this.emit("room_joined", {
            roomId: msg.roomId,
            role: msg.role,
            opponentName: msg.opponentName,
            peerId: msg.peerId,
          });
          break;
        }

        case "peer_joined": {
          this.currentRoomId = msg.roomId;
          this.emit("peer_joined", {
            roomId: msg.roomId,
            role: msg.role,
            opponentName: msg.opponentName,
            peerId: msg.peerId,
          });
          break;
        }

        case "peer_left": {
          this.emit("peer_left", msg.reason, msg.peerId);
          break;
        }

        case "room_peers_sync": {
          this.currentRoomId = msg.roomId;
          this.emit("room_peers_sync", msg);
          break;
        }

        case "room_error": {
          this.emit("room_error", msg.message);
          break;
        }

        case "signal_offer": {
          this.emit("signal_offer", msg.offer);
          break;
        }

        case "signal_answer": {
          this.emit("signal_answer", msg.answer);
          break;
        }

        case "signal_ice": {
          this.emit("signal_ice", msg.candidate);
          break;
        }

        case "relay": {
          this.emit("relay_message", msg.payload, msg.fromPeerId);
          break;
        }
      }
    } catch (err) {
      console.error("[Signaling] Message error:", err);
    }
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.pingTimer = window.setInterval(() => {
      this.send({ type: "ping", clientTime: Date.now() });
    }, 4000);
  }

  private stopHeartbeat() {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}
